"use client"

import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { platformStorage } from '@/lib/platform-storage'
/**
 * The signed-in user, as the app sees it.
 *
 * Clerk owns authentication; this store only holds the resolved user so the
 * rest of the app has one place to read it. `ClerkAuthBridge` populates it from
 * the Convex user document and clears it on sign-out.
 *
 * The shape keeps Supabase's field names because every consumer reads `id`,
 * `email` and `user_metadata` — but `id` is now the **Clerk user id**, which is
 * what Convex keys every row on. Consumers use it as a per-user cache key, so
 * what matters is that it is stable and unique, not where it came from.
 */
export interface AuthUser {
  id: string
  email: string
  user_metadata: {
    name?: string
    full_name?: string
    avatar_url?: string
  }
  created_at: string
}

interface AuthState {
  user: AuthUser | null
  isLoading: boolean

  signOut: () => Promise<void>

  setUser: (user: AuthUser | null) => void
  setIsLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        isLoading: true,

        setUser: (user) => set({ user }),
        setIsLoading: (isLoading) => set({ isLoading }),

        signOut: async () => {
          try {
            const clerk = (window as { Clerk?: { signOut?: () => Promise<void> } }).Clerk
            await clerk?.signOut?.()
          } catch (error) {
            console.error("[Auth] Error signing out:", error)
          } finally {
            set({ user: null, isLoading: false })

            // Preserve the keys that should outlive a sign-out: this browser's
            // settings, not anything about the account. `theme` is where
            // next-themes keeps the choice; without it the login page falls
            // back to the app's dark default.
            const preserved: Record<string, string> = {}
            for (const key of [
              "blitztask_whats_new_seen_version",
              "theme",
              "preferred-locale",
              "blitztask.layout",
            ]) {
              const value = localStorage.getItem(key)
              if (value !== null) preserved[key] = value
            }

            localStorage.clear()
            sessionStorage.clear()

            for (const [key, value] of Object.entries(preserved)) {
              localStorage.setItem(key, value)
            }
          }
        },
      }),
      {
        name: 'auth-storage',
        // Named rather than defaulted: zustand falls back to `localStorage`,
        // which does not exist on a phone. Metro resolves the `.native`
        // sibling to AsyncStorage so this store file runs on both clients.
        storage: createJSONStorage(() => platformStorage),
        partialize: (state) => ({ user: state.user }),
      }
    ),
    { name: 'auth-store' }
  )
)
